import { Outlet, useLocation, useNavigate } from "react-router-dom";
import LayoutNavbar from "../components/navbar/navbar";
import useDarkMode from "use-dark-mode";
import { ToastContainer, toast } from "react-toastify";
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/api";
import { useAppDispatch } from "@/hooks/useAppDispatch.ts";
import {
  setChangeLogModal,
  setPermissionScopes,
  setTour,
  setUser,
} from "../store/user/reducer";
import { User } from "@/api/auth/auth.ts";
import { Button, HeroUIProvider } from "@heroui/react";
import { Navigation } from "../components/navigation/navigation";
import { useAppSelector } from "@/hooks/useAppSelector.ts";
import { addNotification } from "../store/notifications/reducer";
import { NotificationIcon } from "../store/notifications/types";
import ym from "react-yandex-metrika";
import { loadHandbooks } from "@/utils/loadHandbook";
import { Tour } from "./tour";
import ChangeLogModal from "../components/modals/change-log/changeLogModal";

interface LayoutProps {
  children?: React.ReactNode;
  withNavbar?: boolean;
  withNavigation?: boolean;
  constrainContent?: boolean;
  contentClassName?: string;
}

export function Layout({
  children,
  withNavbar = true,
  withNavigation = true,
  constrainContent = true,
  contentClassName = "",
}: LayoutProps) {
  const darkMode = useDarkMode(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [user, setDUser] = useState<User | null>(null);
  const renter = useAppSelector((state) => state.location.location);
  const slocation = useLocation();

  useEffect(() => {
    document
      .querySelector("body")
      ?.classList.remove("dark", "text-foreground", "bg-background");
    document
      .querySelector("body")
      ?.classList.add(
        darkMode.value ? "dark" : "white",
        "text-foreground",
        "bg-background",
      );
  }, [darkMode.value]);

  useEffect(() => {
    loadHandbooks({ handbooks: "all" });
  }, []);

  useEffect(() => {
    api.notifications
      .list()
      .then((res) => {
        res.data.forEach((notification) => {
          dispatch(
            addNotification({
              id: notification.id,
              icon: NotificationIcon.INFO,
              title: notification.title,
              message: notification.message,
              datetime: notification.created_at,
              is_read: notification.is_read,
            }),
          );
        });
      })
      .catch((err) => {
        console.log(err);
      });

    api.auth.me().then((data) => {
      setDUser(data.data);

      dispatch(
        setTour({
          isTourActive: !data.data.tour_completed,
        }),
      );

      dispatch(setUser(data.data));
    });

    if (
      location.href.indexOf("selectLocation") == -1 &&
      !localStorage.getItem("locationId")
    ) {
      navigate("/dashboard/selectLocation");
    }

    api.auth.getMyPermissions().then((data) => {
      if (renter.id !== 0) {
        const renterPermissions = data.data.find(
          (el) => el.renter_id == renter.id,
        );
        if (!renterPermissions) return;

        dispatch(setPermissionScopes(renterPermissions.permissions));
      }
    });

    const url = slocation.pathname + slocation.search;
    ym("hit", url);

    loadHandbooks({ handbooks: "all" });
  }, [dispatch, navigate, renter.id, slocation.pathname, slocation.search]);

  const linkTelegram = useCallback(() => {
    api.auth
      .generateTelegramLink()
      .then((data) => {
        location.href = data.data.link;
      })
      .catch((err) => {
        console.error(err);
        toast.error(
          "У нас произошли некоторые проблемы с привязкой вашего аккаунта к Telegram. Попробуйте позже или обратитесь в поддержку",
        );
      });
  }, []);

  const goToSettings = useCallback(() => {
    navigate("/dashboard/settings");
  }, [navigate]);

  const telegramOverlayVisible = useMemo(() => {
    return (
      user &&
      (typeof user.telegram == "undefined" ||
        user.telegram == 0 ||
        user.telegram == null)
    );
  }, [user]);

  const emailOverlayVisible = useMemo(() => {
    return (
      user &&
      (typeof user.email == "undefined" ||
        user.email == null ||
        user.email == "")
    );
  }, [user]);

  useEffect(() => {
    api.changeLogs.getUnread().then((res) => {
      if (res.data.length > 0 && user?.tour_completed) {
        dispatch(setChangeLogModal(true));
      }
    });
  }, [dispatch, user?.tour_completed]);

  return (
    <>
      <ChangeLogModal />
      <HeroUIProvider navigate={navigate}>
        <main
          id="main"
          className={`${darkMode.value ? "dark bg-black" : "bg-zinc-100"} text-foreground lg:max-h-screen`}
        >
          <ToastContainer
            position="top-right"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop={true}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="dark"
          />
          <Tour />
          <div className={withNavbar ? "layout" : "flex min-h-dvh flex-col"}>
            {withNavbar && <LayoutNavbar />}
            <div
              className={
                constrainContent
                  ? "layout__content overflow-y-auto lg:max-h-screen"
                  : `flex flex-1 flex-col w-full ${contentClassName}`
              }
            >
              <div
                className={`${telegramOverlayVisible ? "block" : "hidden"} w-full bg-primary-100 flex flex-col gap-2 rounded-xl p-4 mb-4`}
              >
                <span className="text-sm">
                  Заметили, что у вас всё ещё не привязан Telegram. А зря - у
                  нас есть удобный Telegram-бот, облегчающий работу с кабинетом.
                </span>
                <Button
                  variant="flat"
                  color="primary"
                  size="sm"
                  onPress={linkTelegram}
                  className="mt-2 max-w-fit"
                >
                  Класс, хочу попробовать
                </Button>
              </div>
              <div
                className={`${emailOverlayVisible ? "block" : "hidden"} w-full bg-danger-100 flex flex-col gap-2 rounded-xl p-4 mb-4`}
              >
                <span className="text-sm">
                  Во избежание утери доступа к аккаунту рекомендуем срочно
                  установить адрес электронной почты. Сделать это можно в
                  Настройках.
                </span>
                <Button
                  variant="flat"
                  color="danger"
                  size="sm"
                  onPress={goToSettings}
                  className="mt-2 max-w-fit"
                >
                  Перейти в Настройки
                </Button>
              </div>
              {withNavigation && <Navigation />}
              {children || <Outlet />}
            </div>
          </div>
        </main>
      </HeroUIProvider>
    </>
  );
}
